import { useCallback } from "react"
import type { PreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow"
import { PRE_ORDER_FLOW_TABLE } from "../../../lib/offline/resources/orders/pre-order-flow/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export type CreatePreOrderFlowInput = Partial<PreOrderFlow> & {
  preOrderId: string
  id?: string
}

export type UpdatePreOrderFlowInput = Partial<PreOrderFlow>

export function usePreOrderFlowMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createFlow = useCallback(
    async (input: CreatePreOrderFlowInput): Promise<PreOrderFlow> => {
      const id = input.id ?? crypto.randomUUID()
      const now = new Date().toISOString()
      const row: PreOrderFlow = {
        id,
        companyId: input.companyId ?? "",
        userId: input.userId ?? null,
        employeeId: input.employeeId ?? null,
        preOrderId: input.preOrderId,
        productId: input.productId ?? null,
        partnerId: input.partnerId ?? null,
        unitId: input.unitId ?? null,
        quantity: input.quantity ?? null,
        weight: input.weight ?? null,
        pricePerWeight: input.pricePerWeight ?? null,
        status: input.status ?? 0,
        flowIndex: input.flowIndex ?? null,
        comment: input.comment ?? null,
        hiddenComment: input.hiddenComment ?? null,
        deliveryDate: input.deliveryDate ?? null,
        orderDate: input.orderDate ?? null,
        flowMetadata: input.flowMetadata ?? null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      }
      await storage.put(PRE_ORDER_FLOW_TABLE, row)
      await changeQueue.enqueue("pre-order-flows", {
        op: "create",
        id,
        data: row,
      })
      void engine.pushChanges("pre-order-flows")
      return row
    },
    [storage, changeQueue, engine]
  )

  const updateFlow = useCallback(
    async (id: string, input: UpdatePreOrderFlowInput): Promise<void> => {
      const existing = await storage.get<PreOrderFlow>(PRE_ORDER_FLOW_TABLE, id)
      if (!existing) return
      const updated: PreOrderFlow = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }
      await storage.put(PRE_ORDER_FLOW_TABLE, updated)
      await changeQueue.enqueue("pre-order-flows", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("pre-order-flows")
    },
    [storage, changeQueue, engine]
  )

  const deleteFlow = useCallback(
    async (id: string): Promise<void> => {
      await storage.delete(PRE_ORDER_FLOW_TABLE, id)
      await changeQueue.enqueue("pre-order-flows", { op: "delete", id })
      void engine.pushChanges("pre-order-flows")
    },
    [storage, changeQueue, engine]
  )

  return { createFlow, updateFlow, deleteFlow }
}
